import axios from "axios";
import { END_POINT } from "../constants/defaultValues";

// Store default adapter to fallback when online
const defaultAdapter = typeof axios.getAdapter === "function"
  ? axios.getAdapter(axios.defaults.adapter)
  : axios.defaults.adapter;

const createOfflineUnavailableError = (config, urlPath) => {
  const responseText = `Tính năng này cần kết nối mạng: ${urlPath}`;
  const error = new Error(responseText);
  error.code = "ERR_OFFLINE_UNAVAILABLE";
  error.config = config;
  error.response = {
    data: { success: false, responseText },
    status: 503,
    statusText: "Service Unavailable",
    headers: {},
    config,
  };
  return error;
};

const createOfflineId = () => {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `BILL_OFFLINE_${Date.now()}_${randomPart}`;
};

/**
 * Custom Axios Adapter for Electron Offline Fallback
 */
export const OfflineAxiosAdapter = async function (config) {
  const isElectron = !!window.electronAPI;

  // Check online status via Electron API
  let isOnline = true;
  if (isElectron && window.electronAPI.offline) {
    try {
      isOnline = await window.electronAPI.offline.getStatus();
    } catch (e) {
      isOnline = false;
    }
  }

  // Intercept backend API requests only when offline in Electron
  if (isElectron && !isOnline && config.url && config.url.startsWith(END_POINT)) {
    const urlPath = config.url.replace(END_POINT, "");
    const method = config.method ? config.method.toUpperCase() : "GET";
    const userId = localStorage.getItem("b") || localStorage.getItem("user_id") || "0";

    console.log(`[OfflineAxiosAdapter] Intercepted ${method} ${urlPath}`);

    try {
      let responseData = null;
      const getCachedResponse = async (cacheKey) => {
        if (!window.electronAPI.database.getCachedResponse) return null;
        return window.electronAPI.database.getCachedResponse(cacheKey, userId);
      };
      const getCachedCustomers = async () => {
        const cached = await getCachedResponse("customers");
        return Array.isArray(cached?.data)
          ? cached.data
          : window.electronAPI.database.getCustomers(userId);
      };

      // 1. GET /product/guest
      if (method === "GET" && /^\/product\/guest(?:\?|$)/.test(urlPath)) {
        const cached = await getCachedResponse("products");
        const products = cached?.data || await window.electronAPI.database.getProducts(userId);
        const cachedCategories = await getCachedResponse("categories");
        const categories = Array.isArray(cachedCategories?.data)
          ? cachedCategories.data
          : await window.electronAPI.database.getCategories(userId);
        if (!cached && products.length === 0) {
          throw createOfflineUnavailableError(config, urlPath);
        }
        const urlObj = new URL(config.url, "http://localhost");
        const categoryId = Number.parseInt(urlObj.searchParams.get("categoryId"), 10) || 0;
        const search = (urlObj.searchParams.get("search") || "").trim().toLowerCase();

        // Map categories and build populated products
        const populatedProducts = products.map((p) => {
          const cat = categories.find((c) => c.id === p.category_id);
          return {
            ...p,
            category: p.category || (cat
              ? { id: cat.id, name: cat.name }
              : { id: p.category_id, name: "Chưa phân loại" }),
          };
        });
        const filteredProducts = populatedProducts.filter((product) => {
          const matchesCategory = !categoryId || Number(product.category_id) === categoryId;
          const searchable = `${product.name || ""} ${product.sku || ""} ${product.barcode || ""}`.toLowerCase();
          return matchesCategory && (!search || searchable.includes(search));
        });
        responseData = { ...(cached || {}), success: true, data: filteredProducts };
      }

      // 2. GET /category
      else if (method === "GET" && /^\/category(?:\?|$)/.test(urlPath)) {
        responseData = await getCachedResponse("categories");
        if (!responseData) {
          const categories = await window.electronAPI.database.getCategories(userId);
          responseData = { success: true, data: categories };
        }
      }

      // 3.3 GET /customer/search
      else if (method === "GET" && urlPath.startsWith("/customer/search")) {
        const customers = await getCachedCustomers();
        let keyword = "";
        let phone = "";
        try {
          const urlObj = new URL(config.url, "http://localhost");
          keyword = (urlObj.searchParams.get("keyword") || "").toLowerCase().trim();
          phone = (urlObj.searchParams.get("phone") || "").trim();
        } catch (urlErr) {
          console.warn("[OfflineAxiosAdapter] URL parsing error in search:", urlErr);
        }

        let filtered = customers;
        if (phone) {
          filtered = customers.filter(c => c.phone && c.phone.includes(phone));
        } else if (keyword) {
          filtered = customers.filter(c => 
            (c.name && c.name.toLowerCase().includes(keyword)) || 
            (c.phone && c.phone.includes(keyword)) ||
            (c.email && c.email.toLowerCase().includes(keyword))
          );
        }

        responseData = {
          status: 200,
          success: true,
          responseText: "Success",
          data: filtered
        };
      }

      // 3.4 GET /customer/json
      else if (method === "GET" && urlPath.startsWith("/customer/json")) {
        const customers = await getCachedCustomers();
        let search = "";
        let currentPage = 1;
        let pageSize = 12;
        try {
          const urlObj = new URL(config.url, "http://localhost");
          search = (urlObj.searchParams.get("search") || "").toLowerCase().trim();
          currentPage = parseInt(urlObj.searchParams.get("currentPage"), 10) || 1;
          pageSize = parseInt(urlObj.searchParams.get("pageSize"), 10) || 12;
        } catch (urlErr) {
          console.warn("[OfflineAxiosAdapter] URL parsing error in json list:", urlErr);
        }

        let filtered = customers;
        if (search) {
          filtered = customers.filter(c => 
            (c.name && c.name.toLowerCase().includes(search)) || 
            (c.phone && c.phone.includes(search)) ||
            (c.email && c.email.toLowerCase().includes(search))
          );
        }

        const totalItem = filtered.length;
        const totalPage = Math.ceil(totalItem / pageSize);
        const offset = (currentPage - 1) * pageSize;
        const pageData = filtered.slice(offset, offset + pageSize);

        responseData = {
          status: 200,
          success: true,
          responseText: "Success",
          data: pageData,
          totalPage,
          totalItem
        };
      }

      // 3.6 GET /customer
      else if (method === "GET" && (urlPath === "/customer" || urlPath.startsWith("/customer?"))) {
        responseData = await getCachedResponse("customers");
        if (!responseData) {
          responseData = { status: 200, success: true, responseText: "Success", data: await getCachedCustomers() };
        }
      }

      // 4. GET tables
      else if (method === "GET" && /^\/table\/all_identify(?:\?|$)/.test(urlPath)) {
        responseData = await getCachedResponse("tables");
        if (!responseData) throw createOfflineUnavailableError(config, urlPath);
      }
      else if (method === "GET" && /^\/table(?:\?|$)/.test(urlPath)) {
        const urlObj = new URL(config.url, "http://localhost");
        let floor = urlObj.searchParams.get("floor");
        if (!floor && urlObj.searchParams.get("table_id")) {
          const tableId = urlObj.searchParams.get("table_id");
          const floorList = await getCachedResponse("tables");
          floor = floorList?.data?.find((group) =>
            group.data?.some((table) => String(table.id) === tableId),
          )?.floor;
        }
        responseData = floor !== null && floor !== undefined
          ? await getCachedResponse(`table-floor:${floor}`)
          : null;
        if (!responseData) throw createOfflineUnavailableError(config, urlPath);
      }

      // 6. GET /promotion
      else if (method === "GET" && /^\/promotion\/(?:promotion-active|all)(?:\?|$)/.test(urlPath)) {
        responseData = await getCachedResponse("promotions");
        if (!responseData) {
          const promotions = await window.electronAPI.database.getPromotions(userId);
          responseData = { success: true, data: promotions };
        }
      }

      // 7. GET /setting
      else if (method === "GET" && /^\/setting(?:\?|$)/.test(urlPath)) {
        responseData = await getCachedResponse("settings");
        if (!responseData) {
          const settings = await window.electronAPI.database.getSettings(userId);
          responseData = { success: true, data: settings };
        }
      }

      // 8. GET /user-license/check_accept
      else if (method === "GET" && /^\/user-license\/check_accept(?:\?|$)/.test(urlPath)) {
        responseData = await getCachedResponse("license");
        if (!responseData) throw createOfflineUnavailableError(config, urlPath);
      }

      // 9. Local order reads are required for invoice numbering and printing.
      else if (method === "GET" && /^\/order\/all_orders_today(?:\?|$)/.test(urlPath)) {
        const cached = await getCachedResponse("orders");
        const localOrders = await window.electronAPI.database.getOfflineOrders(userId, {
          todayOnly: true,
        });
        const today = new Date(Date.now() + 7 * 60 * 60 * 1000)
          .toISOString().slice(0, 10);
        const onlineOrders = Array.isArray(cached?.data)
          ? cached.data.filter((order) => order.date === today)
          : [];
        const onlineBillIds = new Set(onlineOrders.map((order) => String(order.bill_id)));
        responseData = {
          success: true,
          data: [...onlineOrders, ...localOrders.filter((order) => !onlineBillIds.has(String(order.server_bill_id)))],
        };
      }
      else if (method === "GET" && /^\/order\/all(?:\?|$)/.test(urlPath)) {
        const cached = await getCachedResponse("orders");
        const localOrders = await window.electronAPI.database.getOfflineOrders(userId, {
          todayOnly: false,
        });
        const onlineOrders = Array.isArray(cached?.data) ? cached.data : [];
        const onlineBillIds = new Set(onlineOrders.map((order) => String(order.bill_id)));
        responseData = {
          success: true,
          data: [...onlineOrders, ...localOrders.filter((order) => !onlineBillIds.has(String(order.server_bill_id)))],
        };
      }

      // 10. POST /bill/guest_order_done or /bill/guest-pending
      else if (
        (urlPath === "/bill/guest_order_done" ||
          urlPath === "/bill/guest-pending") &&
        method === "POST"
      ) {
        const originalPayload =
          typeof config.data === "string" ? JSON.parse(config.data) : config.data;
        if (
          !originalPayload ||
          !Array.isArray(originalPayload.data) ||
          (Array.isArray(originalPayload.deleted_orders) &&
            originalPayload.deleted_orders.length > 0) ||
          originalPayload.data.some((item) => item?.bill?.id)
        ) {
          // Settling an existing provisional invoice needs server order IDs.
          // A local bill ID cannot safely be substituted here.
          throw createOfflineUnavailableError(config, urlPath);
        }
        const isPending = urlPath.startsWith("/bill/guest-pending");
        const payload = isPending
          ? { ...originalPayload, paid: "provisionalInvoice" }
          : originalPayload;
        const billId = createOfflineId();
        const idempotencyKey = `offline-sale:${billId}`;

        await window.electronAPI.database.createOfflineSale({
          billId,
          userId,
          payload,
          idempotencyKey,
        });

        responseData = {
          success: true,
          bill_id: billId,
          responseText: "Lưu hóa đơn offline thành công",
          offline: true,
        };
      }

      // Fallback for unhandled endpoints
      else {
        console.warn(`[OfflineAxiosAdapter] Unhandled offline endpoint: ${urlPath}`);
        throw createOfflineUnavailableError(config, urlPath);
      }

      return {
        data: responseData,
        status: 200,
        statusText: "OK",
        headers: {},
        config: config,
      };
    } catch (error) {
      console.error("[OfflineAxiosAdapter] Error handling offline request:", error);
      return Promise.reject(error);
    }
  }

  // Fallback to default Axios browser network adapter
  return defaultAdapter(config);
};

// Override the global axios defaults adapter
axios.defaults.adapter = OfflineAxiosAdapter;

export default OfflineAxiosAdapter;
